import React, { useEffect, useRef, useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useTranslation } from '../translations/translations';




const AboutTimeline = () => {
    const timelineRef = useRef(null);
    const timelineItemsRef = useRef([]);
    const [activeTimelineItem, setActiveTimelineItem] = useState(0);
    const [timelineProgress, setTimelineProgress] = useState(0);
    const { language } = useLanguage();
    const t = useTranslation(language);

    // Dati della timeline - modifica questi valori per cambiare la timeline
    const timelineData = [
        {
            year: "2011",
            title: t('timeline_item1_title'),
            description: t('timeline_item1_desc')
        },
        {
            year: "2012",
            title: t('timeline_item2_title'),
            description: t('timeline_item2_desc')
        },
        {
            year: "2014",
            title: t('timeline_item3_title'),
            description: t('timeline_item3_desc')
        },
        {
            year: "2017",
            title: t('timeline_item4_title'),
            description: t('timeline_item4_desc')
        },
        {
            year: "2021",
            title: t('timeline_item5_title'),
            description: t('timeline_item5_desc')
        },
        {
            year: "2022",
            title: t('timeline_item6_title'),
            description: t('timeline_item6_desc')
        },
        {
            year: "2025",
            title: t('timeline_item7_title'),
            description: t('timeline_item7_desc')
        }
    ];

    // Effect for observing animation
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('animate-in');
                    }
                });
            },
            { threshold: 0.1 }
        );

        // Observe timeline container
        if (timelineRef.current) {
            observer.observe(timelineRef.current);
        }

        // Observe each timeline item with delay
        timelineItemsRef.current.forEach((item) => {
            if (item) observer.observe(item);
        });

        return () => {
            observer.disconnect();
        };
    }, []);

    // Effect for handling timeline scroll
    useEffect(() => {
        const handleScroll = () => {
            if (!timelineRef.current) return;

            const timelineRect = timelineRef.current.getBoundingClientRect();
            const timelineTop = timelineRect.top;
            const timelineHeight = timelineRect.height;
            const timelineBottom = timelineRect.bottom;

            // Calculate how far through the timeline we've scrolled
            const scrollPosition = window.innerHeight - timelineTop;
            const scrollPercentage = Math.min(Math.max(scrollPosition / (timelineHeight + window.innerHeight), 0), 1);

            // Calcola il progresso della linea della timeline come valore tra 0 e 1
            const viewportProgress = Math.min(Math.max((window.innerHeight - timelineTop) / timelineHeight, 0), 1);
            setTimelineProgress(viewportProgress);

            // Calculate which item should be active based on scroll percentage
            const itemIndex = Math.min(
                Math.floor(scrollPercentage * timelineData.length),
                timelineData.length - 1
            );

            if (itemIndex >= 0) {
                setActiveTimelineItem(itemIndex);
            }

            // Quando si raggiunge la fine della timeline, impostiamo il progresso al 100%
            if (timelineBottom <= window.innerHeight) {
                setTimelineProgress(1);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    return (
        <div
            ref={timelineRef}
            className="opacity-0 transform translate-y-10 transition-all duration-700 delay-300 mb-12"
        >
            <h3
                className="text-3xl md:text-4xl font-bold text-white mb-12 tracking-tight"
                style={{ fontFamily: "'Space Grotesk', 'Archivo', sans-serif" }}
            >
                {t('timelineAbout')}
                <p
                    className="text-2xl md:text-3xl text-white leading-tight opacity-100 transform translate-y-10 transition-all duration-700 delay-100 mb-8 font-light tracking-tight"
                    style={{ fontFamily: "'Space Grotesk', 'Archivo', sans-serif" }}
                >
                    {t('timelineInfo')}
                </p>
            </h3>

            <div className="mt-16 relative">
                {/* Timeline Line - posizionata centralmente */}
                <div className="absolute left-[16px] md:left-1/2 top-0 bottom-0 w-px bg-orange-700 transform md:translate-x-[-0.5px]">
                    {/* Progress Indicator - ora usa timelineProgress invece di activeTimelineItem */}
                    <div
                        className="absolute top-0 w-px bg-accent transition-all duration-500"
                        style={{
                            height: `${timelineProgress * 100}%`,
                            transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)'
                        }}
                    ></div>
                </div>

                {/* Timeline Items */}
                <div className="relative space-y-20">
                    {timelineData.map((item, index) => (
                        <div
                            key={index}
                            ref={el => timelineItemsRef.current[index] = el}
                            className={`flex flex-col md:flex-row items-start opacity-0 transform translate-y-10 transition-all duration-700 ${index <= activeTimelineItem ? 'timeline-active' : ''
                                }`}
                            style={{
                                transitionDelay: `${300 + (index * 100)}ms`,
                            }}
                        >
                            {/* Year - Positioned left on mobile, alternate left/right on desktop */}
                            <div
                                className={`w-full md:w-1/2 mb-4 md:mb-0 text-right md:pr-12 relative pl-10 md:pl-0 ${index % 2 === 0 ? '' : 'md:order-2 md:text-left md:pl-10 md:pr-0'
                                    }`}
                            >
                                <span
                                    className={`${index <= activeTimelineItem ? 'text-accent' : 'text-white/60'
                                        } text-3xl font-bold transition-colors duration-500`}
                                    style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                                >
                                    {item.year}
                                </span>
                            </div>

                            {/* Content - Positioned right on mobile, alternate right/left on desktop */}
                            <div
                                className={`w-full md:w-1/2 pl-10 md:pl-12 ${index % 2 === 0 ? '' : 'md:order-1 md:pr-12 md:text-right md:pl-0'
                                    }`}
                            >
                                <h4
                                    className={`text-xl font-bold mb-2 transition-colors duration-500 ${index <= activeTimelineItem ? 'text-white' : 'text-white/60'
                                        }`}
                                    style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                                >
                                    {item.title}
                                </h4>
                                <p
                                    className={`transition-colors duration-500 ${index <= activeTimelineItem ? 'text-gray-300' : 'text-gray-500'
                                        }`}
                                    style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                                >
                                    {item.description}
                                </p>
                            </div>

                            {/* Dot indicator - posizionato centralmente sulla linea */}
                            <div
                                className={`absolute left-[8px] md:left-1/2 top-3 transform md:translate-x-[-50%] w-[16px] h-[16px] rounded-full bg-white transition-all duration-500 ${index <= activeTimelineItem ? 'border-2 border-accent' : 'opacity-70'
                                    }`}
                            ></div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default AboutTimeline;